import React, { useState, useRef, useCallback, useEffect, memo } from 'react';
import { Leaf, Wheat, MapPin, Star, Plus, Check } from 'lucide-react';
import { optimizedImageUrl } from '../lib/imageOptim';

const ProductCarousel = ({
  products, activeIndex, onIndexChange, onQuickAdd, onProductClick,
  lastAdded, getImage, localize, language, t, isDark,
}) => {
  const [showAdded, setShowAdded] = useState(null);
  const [slideDir, setSlideDir] = useState(null); // 'up' | 'down' | null
  const touchStart = useRef({ x: 0, y: 0, t: 0 });
  // Set when touchEnd detected a swipe → suppresses the synthetic click that
  // would otherwise open the product popup right after a swipe gesture.
  const swipedRef = useRef(false);

  useEffect(() => {
    if (lastAdded) {
      setShowAdded(lastAdded);
      const timer = setTimeout(() => setShowAdded(null), 1500);
      return () => clearTimeout(timer);
    }
  }, [lastAdded]);

  useEffect(() => {
    if (slideDir) {
      const timer = setTimeout(() => setSlideDir(null), 220);
      return () => clearTimeout(timer);
    }
  }, [slideDir]);

  const goTo = useCallback((idx, direction) => {
    if (idx < 0 || idx >= products.length || idx === activeIndex) return;
    setSlideDir(direction);
    onIndexChange(idx);
  }, [activeIndex, products.length, onIndexChange]);

  const handleTouchStart = useCallback((e) => {
    swipedRef.current = false;
    touchStart.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      t: Date.now(),
    };
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dt = Date.now() - touchStart.current.t;

    // Only count vertical swipes (not horizontal)
    if (Math.abs(dy) < Math.abs(dx)) return;

    // On portrait/mobile we allow native page scroll via touchAction:'pan-y',
    // so a "swipe" must be deliberate: large + fast. Small drags just scroll.
    const portrait = typeof window !== 'undefined' && window.innerHeight > window.innerWidth;
    const velocity = Math.abs(dy) / dt;
    const distOk = portrait ? Math.abs(dy) > 90 : Math.abs(dy) > 40;
    const flickOk = portrait
      ? (velocity > 0.6 && Math.abs(dy) > 50)
      : (velocity > 0.3 && Math.abs(dy) > 15);
    if (distOk || flickOk) {
      // Mark this gesture as a swipe so the synthetic click that follows
      // touchend does NOT open the product popup.
      swipedRef.current = true;
      if (dy < 0 && activeIndex < products.length - 1) {
        // Swipe up → next
        goTo(activeIndex + 1, 'up');
      } else if (dy > 0 && activeIndex > 0) {
        // Swipe down → previous
        goTo(activeIndex - 1, 'down');
      }
    }
  }, [activeIndex, products.length, goTo]);

  if (!products.length) return null;

  const product = products[activeIndex];
  const portrait = typeof window !== 'undefined' && window.innerHeight > window.innerWidth;

  const getCardAnim = () => {
    if (!slideDir) return {};
    return {
      animation: `vertSlide${slideDir === 'up' ? 'Up' : 'Down'} 0.2s ease-out`,
    };
  };

  return (
    <div data-testid="product-carousel" style={{
      position: 'relative',
      height: portrait ? 'auto' : '100%',
      minHeight: portrait ? 'auto' : '300px',
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes vertSlideUp {
          from { opacity: 0.5; transform: translateY(50px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes vertSlideDown {
          from { opacity: 0.5; transform: translateY(-50px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Single card — touch events detect vertical swipe.
          On portrait viewports we allow native vertical scrolling so the page
          can scroll if its content overflows (mobile). Swipe-to-navigate still
          fires on touch-end via the threshold check below. */}
      <div
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          minHeight: portrait ? 'auto' : '100%',
          padding: '0 16px 12px', boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column',
          justifyContent: 'flex-start',
          touchAction: portrait ? 'pan-y' : 'none',
          userSelect: 'none', WebkitUserSelect: 'none',
        }}
      >
        <div key={product.id} data-testid={`product-card-${product.id}`}
          onClick={() => {
            if (swipedRef.current) return;
            if (onProductClick) onProductClick(product, activeIndex);
          }}
          role="button"
          tabIndex={0}
          style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer', ...getCardAnim() }}>
          <div style={{
            borderRadius: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column',
            background: isDark ? '#1e1e1e' : '#fff',
            border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(183,140,60,0.1)',
            boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.4)' : '0 8px 30px rgba(0,0,0,0.06)',
          }}>
            {/* Image — fixed height so it always spans full card width */}
            <div
              style={{
                width: '100%', height: 'min(38vh, 360px)', minHeight: '200px',
                position: 'relative',
                overflow: 'hidden', background: isDark ? '#252525' : '#f0ebe0',
              }}
            >
              <img
                src={optimizedImageUrl(getImage(product, activeIndex), 800)}
                alt={localize(product.name)}
                loading="eager"
                decoding="async"
                draggable={false}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', position: 'absolute', inset: 0 }}
              />
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: '100px',
                background: isDark
                  ? 'linear-gradient(transparent, rgba(30,30,30,0.9))'
                  : 'linear-gradient(transparent, rgba(255,255,255,0.7))',
              }} />

              {/* Premium badge */}
              {(product.origin_seal || product.is_premium || product.badge_premium) && (
                <div style={{
                  position: 'absolute', top: '12px', left: '12px',
                  padding: '7px 14px', borderRadius: '22px',
                  background: 'linear-gradient(135deg, #D4AF37, #F5D061, #D4AF37)',
                  color: '#1a1a1a', fontSize: '16px', fontWeight: 700,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                }}>
                  <Star size={14} fill="currentColor" style={{ display: 'inline', marginRight: '6px', verticalAlign: '-2px' }} />
                  {typeof product.origin_seal === 'string' ? product.origin_seal : (product.origin_seal?.[language] || product.origin_seal?.es || 'Premium')}
                </div>
              )}

              {(product.badge_bestseller || product.is_featured) && (
                <div style={{
                  position: 'absolute', top: '12px', right: '12px',
                  padding: '7px 14px', borderRadius: '22px',
                  background: 'linear-gradient(135deg, #B78C3C, #D4AF37)',
                  color: '#fff', fontSize: '16px', fontWeight: 700, textTransform: 'uppercase',
                }}>
                  Top
                </div>
              )}

              {product.badge_nuevo && !product.badge_bestseller && !product.is_featured && (
                <div style={{
                  position: 'absolute', top: '12px', right: '12px',
                  padding: '7px 14px', borderRadius: '22px',
                  background: 'linear-gradient(135deg, #10B981, #34D399)',
                  color: '#fff', fontSize: '16px', fontWeight: 700,
                }}>
                  {language === 'es' ? 'Nuevo' : language === 'en' ? 'New' : language === 'cat' ? 'Nou' : 'Nouveau'}
                </div>
              )}

              {/* Counter on image */}
              {products.length > 1 && (
                <div style={{
                  position: 'absolute', bottom: '12px', right: '12px',
                  padding: '6px 14px', borderRadius: '14px',
                  background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
                  fontFamily: "'DM Sans', sans-serif", fontSize: '18px', fontWeight: 600, color: '#fff',
                }}>
                  {activeIndex + 1} / {products.length}
                </div>
              )}
            </div>

            {/* Content */}
            <div style={{ padding: '18px 22px 10px' }}>
              {(product.is_vegan || product.badge_vegano || product.is_gluten_free || product.badge_sin_gluten || product.is_local || product.badge_local) && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  {(product.is_vegan || product.badge_vegano) && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '18px', fontWeight: 500, color: '#4ade80', background: isDark ? 'rgba(74,222,128,0.1)' : 'rgba(74,222,128,0.08)', padding: '6px 14px', borderRadius: '22px' }}>
                      <Leaf size={16} /> Vegan
                    </span>
                  )}
                  {(product.is_gluten_free || product.badge_sin_gluten) && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '18px', fontWeight: 500, color: '#f59e0b', background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.08)', padding: '6px 14px', borderRadius: '22px' }}>
                      <Wheat size={16} /> GF
                    </span>
                  )}
                  {(product.is_local || product.badge_local) && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '18px', fontWeight: 500, color: isDark ? '#D4AF37' : '#B78C3C', background: isDark ? 'rgba(212,175,55,0.1)' : 'rgba(183,140,60,0.06)', padding: '6px 14px', borderRadius: '22px' }}>
                      <MapPin size={16} /> Local
                    </span>
                  )}
                </div>
              )}

              <h3
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(26px, 3.6vw, 39px)', fontWeight: 600, lineHeight: 1.2,
                  color: isDark ? '#f5f5f5' : '#2C2C2A',
                  marginBottom: '8px',
                }}
              >
                {localize(product.name)}
              </h3>

              {product.description && (
                <p style={{
                  fontFamily: "'DM Sans', sans-serif", fontSize: '18px', lineHeight: 1.4,
                  color: isDark ? 'rgba(255,255,255,0.65)' : '#6B6B68',
                  display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden', marginBottom: '4px',
                }}>
                  {localize(product.description)}
                </p>
              )}
            </div>

            {/* Pill button (price + plus) — left-aligned to avoid overlap with Emergent badge */}
            <div style={{ padding: '8px 20px 16px', display: 'flex' }}>
              <button
                onClick={(e) => { e.stopPropagation(); onQuickAdd(e, product); }}
                data-testid={`quick-add-${product.id}`}
                className={`quick-add-btn-pill ${showAdded === product.id ? 'added' : ''}`}
                aria-label={language === 'es' ? 'Añadir' : language === 'en' ? 'Add' : language === 'cat' ? 'Afegir' : 'Ajouter'}
              >
                <span className="qa-price">{product.price?.toFixed(2)} €</span>
                <span className="qa-icon">{(showAdded === product.id) ? <Check size={16} /> : <Plus size={16} />}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Vertical dot indicators (right side) */}
      {products.length > 1 && (
        <div style={{
          position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
          display: 'flex', flexDirection: 'column', gap: '5px', zIndex: 5,
        }}>
          {products.map((p, idx) => (
            <button
              key={p.id}
              onClick={() => goTo(idx, idx > activeIndex ? 'up' : 'down')}
              data-testid={`carousel-dot-${idx}`}
              style={{
                width: '5px',
                height: idx === activeIndex ? '18px' : '5px',
                borderRadius: '3px', border: 'none', padding: 0, cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                background: idx === activeIndex
                  ? 'linear-gradient(180deg, #B78C3C, #D4AF37)'
                  : isDark ? 'rgba(255,255,255,0.2)' : '#ddd',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(ProductCarousel);
